// Implements: F-IDN-012, F-IDN-025
/**
 * "Dónde participas" of the v3 Perfil card (`acctProfile`, owner 2026-10-19): one row per
 * organization or team the person holds a seat in, with an icon for its kind, its name and the
 * person's role there as a badge. Pure presentation over the person's own scopes; the platform
 * scope is not a place one participates in and is left out. An empty list says so in words.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Icon } from "../../../shared/ui/atoms/Icon";
import type { ScopeEntry } from "../../../types/api";
import shared from "./account.module.css";
import styles from "./ParticipationList.module.css";

/** The scope roles the server publishes over organizations and teams, by their label. */
const ROLE: Readonly<
  Record<
    string,
    | "organization.role.orgAdmin"
    | "organization.role.orgMember"
    | "organization.role.teamLead"
    | "organization.role.teamMember"
  >
> = {
  org_admin: "organization.role.orgAdmin",
  org_member: "organization.role.orgMember",
  team_lead: "organization.role.teamLead",
  team_member: "organization.role.teamMember",
};

export interface ParticipationListProps {
  /** The person's own scopes, in the server's order (organizations, then teams, by name). */
  readonly scopes: readonly ScopeEntry[];
}

export function ParticipationList({ scopes }: ParticipationListProps): ReactElement {
  const { t } = useTranslation();
  const labelId = useId();
  const places = scopes.filter((scope) => scope.kind === "organization" || scope.kind === "team");
  return (
    <div className={shared.field}>
      <span className={shared.label} id={labelId}>
        {t("account.participation.title")}
      </span>
      {places.length === 0 ? (
        <p className={shared.help}>{t("account.participation.empty")}</p>
      ) : (
        <ul className={styles.list} aria-labelledby={labelId}>
          {places.map((scope) => {
            const role = ROLE[scope.role];
            return (
              <li className={styles.row} key={`${scope.kind}-${scope.id ?? ""}`}>
                <Icon name={scope.kind === "team" ? "users" : "building"} size="sm" />
                <span className={styles.name}>{scope.name ?? t("account.participation.unnamed")}</span>
                {role === undefined ? null : <Badge tone="neutral">{t(role)}</Badge>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
